import { getIdToken } from "src/domains/shared/utils/amplifyCompat";

/** 서버 계약의 청크 크기 — 512KB 고정, 마지막 청크만 잔여 크기 허용(비-마지막은 512KB 배수 offset). */
export const UPLOAD_CHUNK_SIZE = 512 * 1024;

const CHUNK_RETRY_ATTEMPTS = 3;
const CHUNK_RETRY_BASE_MS = 500;
const HTTP_INTERNAL_SERVER_ERROR = 500;

/** 청크 응답 공통 필드 — 완료 식별자 이름은 엔드포인트마다 달라 pickCompletion 이 꺼낸다. */
interface ChunkResponse {
  token?: string | null;
  durable_offset?: number | null;
  [key: string]: unknown;
}

interface ChunkParams {
  chunk: Blob;
  offset: number;
  fileName?: string;
  token?: string;
  last: boolean;
  fields?: Record<string, string>;
}

export interface UploadInChunksParams<T> {
  /** backendBaseUrl() 이 포함된 절대 URL. */
  url: string;
  /** 업로드 파일명 — stem 이 서버 측 식별자가 된다. NFD 정규화는 호출 측 책임. */
  fileName: string;
  /** 원본 바이너리. */
  file: File;
  /** 마지막 청크 응답에서 완료 식별자를 꺼낸다 — falsy 면 프로토콜 위반으로 즉시 실패. */
  pickCompletion: (data: ChunkResponse) => T | null | undefined;
  /** 모든 실패 Error 메시지의 접두 — HTTP 실패·완료 식별자 누락 양쪽에 붙는다 (예: "dwg 업로드", "gis 업로드"). */
  label: string;
  /**
   * **모든** 청크에 함께 실리는 엔드포인트 고유 필드 — 첫 청크 전용인 `file_name` 과 달리 매 요청에
   * 붙는다(예: 정렬 업로드의 `drawing_name` — 토큰이 실어 온 키가 그 도면의 슬롯인지 대조하는 scope
   * 가드라 중간 청크에도 있어야 한다).
   */
  fields?: Record<string, string>;
}

/**
 * main-api는 "Bearer <token>" 형식 필요 — Amplify gateway(Bearer 없는 raw token)와 다르므로
 * API.post 대신 raw fetch + getIdToken() 으로 직접 호출한다.
 * multipart 전송이므로 FormData 를 body 로 넘기고 Content-Type 은 명시하지 않는다
 * (브라우저가 boundary 포함 자동 설정 — 명시하면 boundary 누락으로 400).
 */
async function postChunk(
  url: string,
  params: ChunkParams,
  label: string
): Promise<ChunkResponse> {
  const token = await getIdToken();

  const formData = new FormData();
  formData.append("chunk", params.chunk);
  formData.append("offset", String(params.offset));
  if (params.fileName !== undefined)
    formData.append("file_name", params.fileName);
  if (params.token !== undefined) formData.append("token", params.token);
  if (params.last) formData.append("last", "true");
  // 규약 필드를 **덧붙이는** 것이지 대체하지 않는다 — `append` 라 위의 `chunk`/`offset`/`token` 은
  // 어떤 `fields` 를 받아도 FormData 에 그대로 남는다.
  for (const [name, value] of Object.entries(params.fields ?? {}))
    formData.append(name, value);

  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: token },
    body: formData,
  });

  if (!res.ok) {
    const detail = await failureDetail(res);
    throw Object.assign(
      new Error(
        detail
          ? `${label} 실패 (${res.status}): ${detail}`
          : `${label} 실패 (${res.status})`
      ),
      { status: res.status, detail }
    );
  }

  const body = await res.json();
  // 응답 봉투 { status, data } 언랩
  return body.data ?? body;
}

/**
 * 거부 응답의 사유 — 봉투의 `message`(또는 `detail`)를 있는 그대로 꺼낸다. 없거나 JSON 이 아니면 `undefined`.
 *
 * 사유가 있어야 호출 측이 「무엇이 왜 거부됐는지」를 말할 수 있다 — 묶음 zip 의 409 는 겹친 도면
 * 이름을 본문에 싣는데, 상태 코드만 올리면 그 이름들이 화면에 닿지 못한다.
 */
async function failureDetail(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as {
      message?: unknown;
      detail?: unknown;
      data?: { message?: unknown };
    };
    const message = body.message ?? body.detail ?? body.data?.message;
    return typeof message === "string" && message ? message : undefined;
  } catch {
    return undefined;
  }
}

/** 5xx·네트워크 오류(status 없음)만 청크 단위 재시도 — 400(형식)/409(이름 충돌)/413(한도)은 즉시 실패. */
function isRetryableChunkError(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  if (status === undefined) return true;
  return status >= HTTP_INTERNAL_SERVER_ERROR;
}

async function sendChunkWithRetry(
  url: string,
  params: ChunkParams,
  label: string
): Promise<ChunkResponse> {
  let lastError: unknown;
  for (let attempt = 0; attempt < CHUNK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await postChunk(url, params, label);
    } catch (error) {
      lastError = error;
      if (
        isRetryableChunkError(error) === false ||
        attempt === CHUNK_RETRY_ATTEMPTS - 1
      ) {
        throw error;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, CHUNK_RETRY_BASE_MS * (attempt + 1))
      );
    }
  }
  throw lastError;
}

/**
 * 512KB 고정 청크 순차 업로드. 첫 청크(token 없음, `file_name` 동봉)로 세션을 시작해 token 을 받고,
 * 마지막 청크(last=true) 응답에서 `pickCompletion` 이 꺼낸 완료 식별자로 완료를 판별해 반환한다.
 * 다음 청크의 offset 은 직전 응답의 `durable_offset` 이 SoT — 로컬 누적치로 진행하지 않아
 * 서버에 내구 저장되지 않은 청크를 자연 재전송한다.
 * 업로드 재개(토큰 영속화)는 의도적으로 도입하지 않는다 — 세션 내 재시도만 지원.
 */
export async function uploadInChunks<T>({
  url,
  fileName,
  file,
  pickCompletion,
  label,
  fields,
}: UploadInChunksParams<T>): Promise<T> {
  let token: string | undefined;
  let offset = 0;

  for (;;) {
    const end = Math.min(offset + UPLOAD_CHUNK_SIZE, file.size);
    const last = end >= file.size;
    const isFirstChunk = token === undefined;
    const data = await sendChunkWithRetry(
      url,
      {
        chunk: file.slice(offset, end),
        offset,
        fileName: isFirstChunk ? fileName : undefined,
        token,
        last,
        fields,
      },
      label
    );
    if (last) {
      const completion = pickCompletion(data);
      if (!completion) {
        // last=true 인데 완료 식별자가 없으면 프로토콜 위반 — 재전송 루프에 빠지지 않게 즉시 실패.
        throw new Error(`${label}가 확정되지 않았습니다.`);
      }
      return completion;
    }
    if (isFirstChunk) token = data.token ?? undefined;
    offset = data.durable_offset ?? end;
  }
}
